import { can, type Permission, type PlanFeature, type StaffRole } from "@luckymenu/core";

/**
 * Which sections a role sees, on the restaurant's plan. The same model drives
 * both staff apps; each maps keys to its own routes and icons, and translates
 * `labelKey` from the `nav` namespace. A section whose plan features are all
 * missing is hidden (P10-T04, platform-billing.md, Plans). The server still
 * checks every call. See ux-design.md.
 */

export type TabKey = "orders" | "tables" | "bill" | "menu" | "more";

export type MoreKey =
	| "restaurantProfile"
	| "bills"
	| "dayClose"
	| "reports"
	| "activityLog"
	| "offers"
	| "tablesAndQr"
	| "orderingSettings"
	| "billingSettings"
	| "printers"
	| "staff"
	| "settings"
	| "subscription"
	| "device"
	| "switchPerson"
	| "switchRestaurant"
	| "signOut";

type Entry<K> = {
	key: K;
	labelKey: string;
	requires: Permission | null;
	/** Plan features of which one is enough, as the procedures behind the section ask. */
	features?: readonly PlanFeature[];
};

/** Orders taken by staff and the board work with QR ordering or billing (platform-billing.md). */
const ORDERING: readonly PlanFeature[] = ["qr_ordering", "billing"];

const TABS: Entry<TabKey>[] = [
	{ key: "orders", labelKey: "orders", requires: "order.view", features: ORDERING },
	{ key: "tables", labelKey: "tables", requires: "order.create", features: ORDERING },
	{ key: "bill", labelKey: "bill", requires: "bill.create", features: ["billing"] },
	{ key: "menu", labelKey: "menu", requires: "menu.availability" },
	{ key: "more", labelKey: "more", requires: null },
];

const MORE: Entry<MoreKey>[] = [
	{ key: "restaurantProfile", labelKey: "restaurantProfile", requires: "settings.manage" },
	{ key: "bills", labelKey: "bills", requires: "bill.create", features: ["billing"] },
	{ key: "dayClose", labelKey: "dayClose", requires: "day.close", features: ["billing"] },
	{ key: "reports", labelKey: "reports", requires: "reports.view", features: ["reports"] },
	{ key: "activityLog", labelKey: "activityLog", requires: "audit.view" },
	{ key: "offers", labelKey: "offers", requires: "offers.manage", features: ["offers"] },
	{ key: "tablesAndQr", labelKey: "tablesAndQr", requires: "tables.manage" },
	{ key: "orderingSettings", labelKey: "orderingSettings", requires: "settings.manage" },
	{
		key: "billingSettings",
		labelKey: "billingSettings",
		requires: "settings.manage",
		features: ["billing"],
	},
	{
		key: "printers",
		labelKey: "printers",
		requires: "printers.manage",
		features: ["printing"],
	},
	{ key: "staff", labelKey: "staff", requires: "staff.manage" },
	{ key: "settings", labelKey: "settings", requires: "settings.manage" },
	{ key: "subscription", labelKey: "subscription", requires: "subscription.view" },
	// Notifications on this device, on duty; on Android also battery, counter mode (P6-T06 to T10).
	{ key: "device", labelKey: "device", requires: null },
	// Staff PIN and quick switch on a shared device (OP-19, P6-T11).
	{ key: "switchPerson", labelKey: "switchPerson", requires: null },
	{ key: "switchRestaurant", labelKey: "switchRestaurant", requires: null },
	{ key: "signOut", labelKey: "signOut", requires: null },
];

export type NavigationModel = {
	tabs: { key: TabKey; labelKey: string }[];
	more: { key: MoreKey; labelKey: string }[];
};

/** Whether the plan includes one of `needed`; no list, or no plan known yet, is everything. */
export function planIncludes(
	features: readonly PlanFeature[] | undefined,
	needed: PlanFeature | readonly PlanFeature[] | undefined,
): boolean {
	if (!features || needed === undefined) return true;
	const list: readonly PlanFeature[] = typeof needed === "string" ? [needed] : needed;
	return list.length === 0 || list.some((f) => features.includes(f));
}

export function navigationFor(
	role: StaffRole,
	options: {
		restaurantCount?: number;
		/** The plan's features (`getCurrent().entitlements`); left out, nothing is hidden for the plan. */
		features?: readonly PlanFeature[];
	} = {},
): NavigationModel {
	const allowed = <K>(e: Entry<K>) =>
		(e.requires === null || can(role, e.requires)) && planIncludes(options.features, e.features);
	const strip = <K>({ key, labelKey }: Entry<K>) => ({ key, labelKey });
	return {
		tabs: TABS.filter(allowed).map(strip),
		more: MORE.filter(allowed)
			.filter((e) => e.key !== "switchRestaurant" || (options.restaurantCount ?? 1) > 1)
			.map(strip),
	};
}

/**
 * What the Orders tab opens on: the live board, or for the kitchen (who
 * neither decides on diners' orders nor takes orders) the KOT list.
 */
export function ordersHome(role: StaffRole): "board" | "kitchen" {
	return can(role, "batch.decide") || can(role, "order.create") ? "board" : "kitchen";
}

const TAB_KEYS: readonly string[] = TABS.map((t) => t.key);

/**
 * The tab to open instead of `tab` when the role or the plan does not have
 * it (P10-T04: a menu-only plan has no Orders, Tables or Bill): the first tab
 * there is. Null when `tab` is fine or is not a tab. Both apps send a hidden
 * tab's address here, and open the first tab after sign-in.
 */
export function tabFallback(model: NavigationModel, tab: string | undefined): TabKey | null {
	if (!tab || !TAB_KEYS.includes(tab)) return null;
	if (model.tabs.some((t) => t.key === tab)) return null;
	return model.tabs[0]?.key ?? null;
}
